"use client";

import { useState } from "react";
import { ChevronDown, ChevronUp, Trash2, ArrowUp, ArrowDown, Filter, Shuffle } from "lucide-react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import type { IChangeEvent } from "@rjsf/core";
import type { RJSFSchema } from "@rjsf/utils";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import type { RouteStep, StepCatalogEntry, StepCategory } from "@/lib/types/routing";
import { isPlanSufficient } from "@/lib/plans";
import { uiSchemaFor } from "./step-ui-schemas";

const CAT_COLORS: Record<StepCategory, string> = {
  Filter: "border-chart-1/40 text-chart-1",
  Transform: "border-chart-3/40 text-chart-3",
};

const CAT_ICONS: Record<StepCategory, typeof Filter> = {
  Filter,
  Transform: Shuffle,
};

interface StepCardProps {
  step: RouteStep;
  catalogEntry?: StepCatalogEntry;
  index: number;
  total: number;
  userPlan: string;
  onChange: (step: RouteStep) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export function StepCard({
  step,
  catalogEntry,
  index,
  total,
  userPlan,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: StepCardProps) {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(step.config, null, 2)
  );

  const category = catalogEntry?.category ?? "Filter";
  const hasSchema =
    catalogEntry?.configSchema &&
    typeof catalogEntry.configSchema === "object" &&
    Object.keys(catalogEntry.configSchema).length > 0;

  const planRequired = catalogEntry?.planRequired ?? "Starter";
  const planSufficient = isPlanSufficient(planRequired, userPlan);
  const CatIcon = CAT_ICONS[category] ?? Filter;
  const catColor = CAT_COLORS[category] ?? "";

  const handleSchemaChange = (e: IChangeEvent) => {
    onChange({ ...step, config: e.formData ?? {} });
  };

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange({ ...step, config: parsed });
    } catch {
      setJsonError("Invalid JSON");
    }
  };

  return (
    <div className="border border-border bg-background">
      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Reorder */}
        <div className="flex flex-col gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onMoveUp}
            disabled={index === 0}
            aria-label={t("dashboard.routes.stepUpLabel")}
          >
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label={t("dashboard.routes.stepDownLabel")}
          >
            <ArrowDown className="h-3 w-3" />
          </Button>
        </div>

        {/* Category badge */}
        <span
          className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] ${catColor}`}
        >
          <CatIcon className="h-2.5 w-2.5" />
          {category}
        </span>

        {/* Step type */}
        <span className="flex-1 text-sm font-medium">{step.type}</span>

        {/* Plan restriction */}
        {!planSufficient && (
          <span className="font-mono text-[9px] uppercase tracking-wider border border-warning/40 text-warning px-1.5 py-0.5">
            {t("dashboard.routes.stepPlanRequired")}
          </span>
        )}

        {/* Enabled toggle */}
        <Switch
          checked={step.enabled}
          onCheckedChange={(checked) => onChange({ ...step, enabled: checked })}
          disabled={!planSufficient}
          aria-label={t("dashboard.routes.stepEnabledLabel")}
        />

        {/* Expand */}
        {hasSchema && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => setExpanded((v) => !v)}
            aria-label={t("dashboard.routes.stepEditConfig")}
          >
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </Button>
        )}

        {/* Remove */}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="text-destructive hover:text-destructive"
          onClick={onRemove}
          aria-label={t("dashboard.routes.stepDeleteLabel")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Expanded config */}
      {expanded && hasSchema && (
        <div className="border-t border-border px-3 py-3">
          {catalogEntry?.configSchema ? (
            <div className="rjsf-minimal">
              <Form
                schema={catalogEntry.configSchema as RJSFSchema}
                formData={step.config}
                validator={validator}
                onChange={handleSchemaChange}
                uiSchema={{
                  "ui:submitButtonOptions": { norender: true },
                  ...uiSchemaFor(step.type),
                }}
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                {t("dashboard.routes.stepEditConfig")} (JSON)
              </label>
              <textarea
                value={jsonText}
                onChange={(e) => handleJsonChange(e.target.value)}
                rows={6}
                className="w-full border border-border bg-transparent px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                spellCheck={false}
              />
              {jsonError && (
                <p className="text-xs text-destructive">{jsonError}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
